import { useEffect, useRef, useState } from 'react';
import { CHAT_SURFACE } from '@/config/environment';
// Only the two idle sheets are bundled into the chat code. Every other pose is a
// dynamic import below, so a page that never waves never fetches the wave sheet.
import idle112 from '@/assets/mascot/emjae-idle-112.webp';
import idle64 from '@/assets/mascot/emjae-idle-64.webp';
import { prefersReducedMotion, type EmjaeMascotPose } from './emjaeMascotMotion';

export type SpriteSheet = '112' | '64';

const SHEET_URL: Record<string, () => Promise<string>> = {
  'idle-112': () => Promise.resolve(idle112),
  'idle-64': () => Promise.resolve(idle64),
  'wave-112': () => import('@/assets/mascot/emjae-wave-112.webp').then((m) => m.default),
  'wave-64': () => import('@/assets/mascot/emjae-wave-64.webp').then((m) => m.default),
  'talking-112': () => import('@/assets/mascot/emjae-talking-112.webp').then((m) => m.default),
  'talking-64': () => import('@/assets/mascot/emjae-talking-64.webp').then((m) => m.default),
  'thinking-112': () => import('@/assets/mascot/emjae-thinking-112.webp').then((m) => m.default),
  'thinking-64': () => import('@/assets/mascot/emjae-thinking-64.webp').then((m) => m.default),
  'happy-112': () => import('@/assets/mascot/emjae-happy-112.webp').then((m) => m.default),
  'happy-64': () => import('@/assets/mascot/emjae-happy-64.webp').then((m) => m.default),
};

/** How long a pose waits for its sheet before it is shown anyway. */
const HOLD_CAP_MS = 2000;

// What the warm-up learned, page-wide (it runs once per page load): sheets that loaded, sheets that failed.
const readySheets = new Set<string>();
const failedSheets = new Set<string>();

/**
 * The sprite art for a pose: whether the sprite can be used, and which pose's art to show.
 *
 * A sheet is probed once per component instance, the first time its pose is shown, with an
 * `Image` (so the browser fetches and caches it; `load` says it is there, `error` that it is
 * unusable). Only the pose being asked for is probed: at first render that is the idle sheet, and
 * wave, talking, thinking and happy load the first time they play.
 *
 * No blank flash: when the pose changes, the previous pose's art stays on screen until the new
 * sheet's probe has loaded (or the warm-up already loaded it, or an earlier use did, which switches
 * in the same render). The idle sheet is bundled, so idle never waits. A sheet that failed (here or
 * in the warm-up) is not waited for: `usable` turns false and the caller shows the still image at
 * the same size. State is per instance on purpose: nothing survives an unmount, and nothing is
 * probed after one.
 */
export function useSpriteArt(
  sheet: SpriteSheet | undefined,
  pose: EmjaeMascotPose,
): { usable: boolean; shownPose: EmjaeMascotPose } {
  const probed = useRef<Set<string>>(new Set());
  const mounted = useRef(false);
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());
  const [loaded, setLoaded] = useState<ReadonlySet<string>>(() => new Set());

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!sheet) return;
    const key = `${pose}-${sheet}`;
    if (probed.current.has(key)) return;
    probed.current.add(key);
    const markFailed = () => {
      if (mounted.current) setFailed((previous) => new Set(previous).add(key));
    };
    const markLoaded = () => {
      if (mounted.current) setLoaded((previous) => new Set(previous).add(key));
    };
    SHEET_URL[key]()
      .then((url) => {
        if (!mounted.current) return;
        const probe = new Image();
        probe.onload = markLoaded;
        probe.onerror = markFailed;
        probe.src = url;
      })
      .catch(markFailed);
  }, [sheet, pose]);

  const key = `${pose}-${sheet}`;
  const hasFailed = (k: string) => failed.has(k) || failedSheets.has(k);
  // A failed sheet is settled too: the still image takes over rather than waiting forever.
  // The hold has a way out: a request that neither loads nor errors (a stalled connection) is given
  // up on after HOLD_CAP_MS, and the pose shows (the still image if it failed meanwhile).
  const [gaveUp, setGaveUp] = useState<string | null>(null);
  const settled =
    !sheet || pose === 'idle' || loaded.has(key) || readySheets.has(key) || hasFailed(key) || gaveUp === key;
  const [shown, setShown] = useState<EmjaeMascotPose>(() => (settled ? pose : 'idle'));
  if (settled && shown !== pose) setShown(pose);
  const shownPose = settled ? pose : shown;

  useEffect(() => {
    if (settled) return undefined;
    const timer = setTimeout(() => setGaveUp(key), HOLD_CAP_MS);
    return () => clearTimeout(timer);
  }, [settled, key]);

  return { usable: Boolean(sheet) && !hasFailed(`${shownPose}-${sheet}`), shownPose };
}

// ---- Warming the cache -----------------------------------------------------------------------
// The non-idle sheets are fetched lazily. After the first paint, once per page load and when the
// browser is idle, they are fetched into the cache, so most poses switch in the same render.

const WARM_POSES = ['thinking', 'talking', 'happy', 'wave'] as const;
const WARM_DELAY_MS = 1500;
let warmScheduled = false;

function warmSheet(key: string): void {
  SHEET_URL[key]()
    .then((url) => {
      const image = new Image();
      image.onload = () => readySheets.add(key);
      image.onerror = () => failedSheets.add(key);
      image.src = url;
    })
    .catch(() => failedSheets.add(key));
}

/** Fetches the pose sheets into the cache when the browser is idle, once per page load. */
export function warmPoseSheets(): void {
  if (warmScheduled || typeof window === 'undefined') return;
  warmScheduled = true;
  // Nothing to gain without motion, and nothing to spend on a metered connection.
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  if (connection?.saveData || prefersReducedMotion()) return;
  const run = () => {
    // The 112 px poses only show on the corner bubble; the default header surface never plays them.
    const sheets: SpriteSheet[] = CHAT_SURFACE === 'bubble' ? ['64', '112'] : ['64'];
    for (const sheet of sheets) for (const pose of WARM_POSES) warmSheet(`${pose}-${sheet}`);
  };
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
    .requestIdleCallback;
  if (typeof idle === 'function') idle.call(window, run, { timeout: 5000 });
  else setTimeout(run, WARM_DELAY_MS);
}
